import { Component, OnDestroy, OnInit } from "@angular/core";
import { IUser } from "app/auth/user.model";
import { IonicPage } from "ionic-angular";
import { AuthService } from "../../app/auth/auth.service";
import { NavigationService } from "../../navigation/navigation.service";
import {
  QB_TIERS,
  QualityBoostTierMeta,
  signalLabel,
  tierMeta,
} from "../../providers/quality-boost/models/quality-boost-catalog";
import { QualityBoostChart } from "../../providers/quality-boost/models/quality-boost-chart";
import { QualityBoostSummary } from "../../providers/quality-boost/models/quality-boost-summary";
import { QualityBoostService } from "../../providers/quality-boost/quality-boost.service";

type SortDirection = "asc" | "desc";

// SignalOption is a selectable entry in the Signal Type filter.
interface SignalOption {
  id: string;
  label: string;
}

// QualityBoostQueuePage is the Prioritized QA Queue (REQ-UI-001): a per-tenant
// list of scored charts tiered High -> Eva Passed, with tier / signal /
// time-sensitive filters, free-text search, and sortable columns. It reads the
// PHI-free QualityBoost dashboard contract (REQ-DASH-001/003) via
// QualityBoostService; tenant isolation and RBAC are enforced server-side
// (REQ-UI-007/008) — this surface is a thin layer over that authorization.
@IonicPage({})
@Component({
  selector: "page-quality-boost-queue",
  templateUrl: "quality-boost-queue.html",
})
export class QualityBoostQueuePage implements OnInit, OnDestroy {
  user: IUser | null = null;

  // Raw data from the API.
  summary: QualityBoostSummary | null = null;
  charts: QualityBoostChart[] = [];

  // Derived view state.
  filteredCharts: QualityBoostChart[] = [];
  tiers: QualityBoostTierMeta[] = QB_TIERS;
  signalOptions: SignalOption[] = [];

  // Filter state (REQ-UI-001). Time-sensitive is the v1 High-tier callout proxy.
  selectedTier = "";
  selectedSignal = "";
  timeSensitiveOnly = false;
  // hideEvaPassed toggles the low-risk-high-confidence (Eva Passed) charts in/out
  // of the queue (REQ-UI-002, AC-UI-002-3). They are shown by default —
  // de-emphasized, not hidden, so they stay auditable (AC-UI-002-2).
  hideEvaPassed = false;
  searchText = "";
  // searchApplied is the committed search term (applied on Enter/submit, not
  // as-you-type — AC-UI-001-2 / OQ-CRAWL-48).
  searchApplied = "";

  // Sort state. A null column means the default tier-grouped, score-descending
  // order (AC-UI-001-1); selecting a column breaks tier grouping while active
  // (AC-UI-001-4).
  sortColumn: string | null = null;
  sortDir: SortDirection = "asc";

  loading = false;
  loadError = false;
  private pending = 0;

  constructor(
    private qbService: QualityBoostService,
    private authSvc: AuthService,
    private navSvc: NavigationService
  ) {}

  ngOnInit(): void {
    this.loadQueue();
  }

  // ionViewWillEnter reloads on every entry so a chart's tier change is reflected
  // on next refresh (AC-UI-001-3).
  ionViewWillEnter(): void {
    this.loadQueue();
  }

  ngOnDestroy(): void {
    // No long-lived subscriptions held; each load's subscription self-completes.
  }

  loadQueue(): void {
    this.user = this.authSvc.getUser();
    if (!this.user || this.user.tenantId == null) {
      // No verified tenant -> render nothing rather than another tenant's data.
      this.loadError = true;
      this.summary = null;
      this.charts = [];
      this.filteredCharts = [];
      return;
    }
    const tenantId = this.user.tenantId;
    this.loading = true;
    this.loadError = false;
    this.pending = 2;

    this.qbService.getSummary(tenantId).subscribe(
      (summary) => {
        this.summary = summary;
        this.buildSignalOptions();
        this.settle();
      },
      () => {
        this.loadError = true;
        this.settle();
      }
    );

    this.qbService.getCharts(tenantId).subscribe(
      (resp) => {
        this.charts = (resp && resp.charts) || [];
        this.applyFilters();
        this.settle();
      },
      () => {
        this.loadError = true;
        this.charts = [];
        this.filteredCharts = [];
        this.settle();
      }
    );
  }

  private settle(): void {
    this.pending = Math.max(0, this.pending - 1);
    if (this.pending === 0) {
      this.loading = false;
    }
  }

  // buildSignalOptions derives the Signal Type filter's options from the signals
  // the scoring run actually surfaced (summary.signalHitCount keys), sorted by
  // label so the dropdown is stable.
  private buildSignalOptions(): void {
    if (!this.summary || !this.summary.signalHitCount) {
      this.signalOptions = [];
      return;
    }
    this.signalOptions = Object.keys(this.summary.signalHitCount)
      .map((id) => ({ id: id, label: signalLabel(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // applySearch commits the typed search text (Enter/submit) then re-filters.
  applySearch(): void {
    this.searchApplied = (this.searchText || "").trim().toLowerCase();
    this.applyFilters();
  }

  // applyFilters recomputes filteredCharts from the raw charts using the current
  // dropdown filters and committed search, then orders the result. Filters and
  // search combine with AND (AC-UI-001-2).
  applyFilters(): void {
    let rows = this.charts.slice();

    if (this.selectedTier) {
      rows = rows.filter((c) => c.tier === this.selectedTier);
    }
    if (this.selectedSignal) {
      rows = rows.filter(
        (c) => (c.firedSignals || []).indexOf(this.selectedSignal) !== -1
      );
    }
    if (this.timeSensitiveOnly) {
      // v1 time-sensitive proxy is the High tier (backend calloutTier).
      rows = rows.filter((c) => c.tier === "high");
    }
    if (this.hideEvaPassed) {
      rows = rows.filter((c) => !this.isEvaPassed(c));
    }
    if (this.searchApplied) {
      const q = this.searchApplied;
      rows = rows.filter((c) => {
        if ((c.chart || "").toLowerCase().indexOf(q) !== -1) {
          return true;
        }
        return (c.firedSignals || []).some(
          (id) => signalLabel(id).toLowerCase().indexOf(q) !== -1
        );
      });
    }

    this.filteredCharts = this.orderRows(rows);
  }

  // orderRows applies the active column sort, or the default tier-grouped,
  // composite-score-descending order when no column sort is active.
  private orderRows(rows: QualityBoostChart[]): QualityBoostChart[] {
    if (!this.sortColumn) {
      // Default: tier rank descending (High first), then score descending within
      // a tier (AC-UI-001-1).
      return rows.sort((a, b) => {
        const rankDelta = tierMeta(b.tier).rank - tierMeta(a.tier).rank;
        if (rankDelta !== 0) {
          return rankDelta;
        }
        return b.score - a.score;
      });
    }

    const dir = this.sortDir === "asc" ? 1 : -1;
    const col = this.sortColumn;
    return rows.sort((a, b) => dir * this.compareByColumn(a, b, col));
  }

  private compareByColumn(
    a: QualityBoostChart,
    b: QualityBoostChart,
    column: string
  ): number {
    switch (column) {
      case "tier":
        return tierMeta(a.tier).rank - tierMeta(b.tier).rank;
      case "score":
        return a.score - b.score;
      case "aged":
        return (a.scoredAt || "").localeCompare(b.scoredAt || "");
      case "chart":
      default:
        return (a.chart || "").localeCompare(b.chart || "");
    }
  }

  // sortBy sets or toggles the active column sort (AC-UI-001-4). The Risk Signals
  // column is intentionally not sortable (Round 2 Q3) and is never passed here.
  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
    } else {
      this.sortColumn = column;
      this.sortDir = "asc";
    }
    this.applyFilters();
  }

  sortIndicator(column: string): string {
    if (this.sortColumn !== column) {
      return "";
    }
    return this.sortDir === "asc" ? "▲" : "▼";
  }

  // clearFilters resets the dropdown filters, search, and any active column sort
  // back to the default tier-grouped, score-descending view (AC-UI-001-2 /
  // Round 2 Q2).
  clearFilters(): void {
    this.selectedTier = "";
    this.selectedSignal = "";
    this.timeSensitiveOnly = false;
    this.hideEvaPassed = false;
    this.searchText = "";
    this.searchApplied = "";
    this.sortColumn = null;
    this.sortDir = "asc";
    this.applyFilters();
  }

  hasActiveSort(): boolean {
    return this.sortColumn !== null;
  }

  // Whether the queue is grouped by tier (default) vs. flat (a column sort is
  // active). Tier grouping breaks while a sort is active (AC-UI-001-4).
  isGrouped(): boolean {
    return !this.sortColumn;
  }

  chartsForTier(tierKey: string): QualityBoostChart[] {
    return this.filteredCharts.filter((c) => c.tier === tierKey);
  }

  // Empty-state discriminators (AC-UI-001-5 / AC-UI-001-6).
  isFilteredToZero(): boolean {
    return (
      !this.loading &&
      !this.loadError &&
      this.filteredCharts.length === 0 &&
      !!this.summary &&
      this.summary.totalCharts > 0
    );
  }

  isNothingSubmitted(): boolean {
    return (
      !this.loading &&
      !this.loadError &&
      !!this.summary &&
      this.summary.totalCharts === 0
    );
  }

  // Display helpers.
  tierLabel(tierKey: string): string {
    return tierMeta(tierKey).label;
  }

  tierColor(tierKey: string): string {
    return tierMeta(tierKey).color;
  }

  tierCount(tierKey: string): number {
    if (!this.summary || !this.summary.tierCounts) {
      return 0;
    }
    return this.summary.tierCounts[tierKey] || 0;
  }

  signalLabelsFor(chart: QualityBoostChart): string[] {
    return (chart.firedSignals || []).map((id) => signalLabel(id));
  }

  // isEvaPassed marks the low-risk-high-confidence charts (the "Eva Passed" tier
  // in v1) that get the de-emphasized treatment (REQ-UI-002, AC-UI-002-1). The
  // exact confidence threshold is deferred (OQ-CRAWL-06, blocked on the Signals
  // composite-score formula); until it lands, the Eva Passed tier is the bucket.
  isEvaPassed(chart: QualityBoostChart): boolean {
    return chart.tier === "passed";
  }

  // perform hands a chart off to review (REQ-UI-004). Full "open the live visit
  // note" behavior belongs to the QA review screen; here we route by chart id.
  perform(chart: QualityBoostChart): void {
    this.navSvc.goToInternalPage("QAPerformPage", { patChartId: chart.chart });
  }
}



// WEBPACK FOOTER //
// ./src/pages/quality-boost-queue/quality-boost-queue.ts